<!--
  @component CacheIO — P3 effecting lens (o-arc).
  chain:   ctx.deployment.dispatch("cache.*", …) → this → ctx.cache
  effects: provision/connect + warm/invalidate/flush + drain + teardown.
           Cache is ephemeral — drain (NOT restore) on takedown.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { CACHE_IO_KEY } from "./deployment-io.js";
  import type { CacheIOContext } from "./deployment-io.js";
  export { CACHE_IO_KEY };
  export type { CacheIOContext };
  export function useCacheIO(): CacheIOContext {
    const ctx = getContext<CacheIOContext | undefined>(CACHE_IO_KEY);
    if (!ctx) throw new Error("useCacheIO() called outside of a CacheIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import type { DeployTargetRef } from "./deployment-io.js";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: CacheIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("cache.")); },
    provision: (target: DeployTargetRef, options?) => dispatchOp("cache.provision", { target, options }),
    connect: (target, options?) => dispatchOp("cache.connect", { target, options }),
    warm: (target, options?) => dispatchOp("cache.warm", { target, options }),
    invalidate: (target, key) => dispatchOp("cache.invalidate", { target, key }),
    flush: (target) => dispatchOp("cache.flush", { target }),
    drain: (target, options?) => dispatchOp("cache.drain", { target, options }),
    teardown: (target, options?) => dispatchOp("cache.teardown", { target, options }),
  } satisfies CacheIOContext;

  setContext(CACHE_IO_KEY, ctx);
</script>
{@render children?.()}
